import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Route as RouteIcon,
  DirectionsBus,
  LocationOn,
  Schedule,
  ToggleOn,
  ToggleOff,
  RemoveCircle,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Itinerarios
 * Path: backoffice-web/src/pages/Itinerarios/Itinerarios.js
 * Última modificación: 2025-01-02 05:45
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Itinerarios() {
  const [itinerarios, setItinerarios] = useState([]);
  const [filteredItinerarios, setFilteredItinerarios] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItinerario, setEditingItinerario] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    paradas: [''],
    distancia_km: '',
    duracion_estimada: '',
    activo: true,
  });
  
  // Cargar itinerarios
  useEffect(() => {
    fetchItinerarios();
  }, []);
  
  // Aplicar filtros
  useEffect(() => {
    let result = [...itinerarios];
    
    // Filtro por búsqueda
    if (searchTerm) {
      result = result.filter(itinerario =>
        itinerario.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        itinerario.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filtro por estado
    if (estadoFilter !== 'todos') {
      result = result.filter(itinerario =>
        estadoFilter === 'activos' ? itinerario.activo : !itinerario.activo
      );
    }
    
    setFilteredItinerarios(result);
    setPage(0);
  }, [itinerarios, searchTerm, estadoFilter]);
  
  const fetchItinerarios = async () => {
    try {
      setLoading(true);
      const response = await api.get('/itinerarios');
      // Manejar diferentes formatos de respuesta del backend
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setItinerarios(data);
    } catch (error) {
      showSnackbar('Error al cargar itinerarios', 'error');
      console.error('Error:', error);
      setItinerarios([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDialog = (itinerario = null) => {
    if (itinerario) {
      setEditingItinerario(itinerario);
      setFormData({
        nombre: itinerario.nombre,
        descripcion: itinerario.descripcion || '',
        paradas: itinerario.paradas || [''],
        distancia_km: itinerario.distancia_km || '',
        duracion_estimada: itinerario.duracion_estimada || '',
        activo: itinerario.activo,
      });
    } else {
      setEditingItinerario(null);
      setFormData({
        nombre: '',
        descripcion: '',
        paradas: [''],
        distancia_km: '',
        duracion_estimada: '',
        activo: true,
      });
    }
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItinerario(null);
    setFormData({
      nombre: '',
      descripcion: '',
      paradas: [''],
      distancia_km: '',
      duracion_estimada: '',
      activo: true,
    });
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Gestión de paradas
  const handleAddParada = () => {
    setFormData(prev => ({
      ...prev,
      paradas: [...prev.paradas, '']
    }));
  };
  
  const handleRemoveParada = (index) => {
    if (formData.paradas.length > 1) {
      setFormData(prev => ({
        ...prev,
        paradas: prev.paradas.filter((_, i) => i !== index)
      }));
    }
  };
  
  const handleParadaChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      paradas: prev.paradas.map((parada, i) => i === index ? value : parada)
    }));
  };
  
  const handleSubmit = async () => {
    try {
      // Validación
      if (!formData.nombre.trim()) {
        showSnackbar('El nombre es requerido', 'warning');
        return;
      }
      
      // Filtrar paradas vacías
      const paradasFiltradas = formData.paradas.filter(p => p.trim() !== '');
      
      if (paradasFiltradas.length < 2) {
        showSnackbar('Debe haber al menos 2 paradas', 'warning');
        return;
      }
      
      setLoading(true);
      
      const dataToSend = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion?.trim() || null,
        paradas: paradasFiltradas,
        distancia_km: formData.distancia_km ? parseFloat(formData.distancia_km) : null,
        duracion_estimada: formData.duracion_estimada ? parseInt(formData.duracion_estimada) : null,
        activo: formData.activo,
      };
      
      if (editingItinerario) {
        await api.put(`/itinerarios/${editingItinerario.id}`, dataToSend);
        showSnackbar('Itinerario actualizado exitosamente', 'success');
      } else {
        await api.post('/itinerarios', dataToSend);
        showSnackbar('Itinerario creado exitosamente', 'success');
      }
      
      handleCloseDialog();
      fetchItinerarios();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al guardar itinerario',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este itinerario?')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/itinerarios/${id}`);
      showSnackbar('Itinerario eliminado exitosamente', 'success');
      fetchItinerarios();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al eliminar itinerario',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleActivo = async (itinerario) => {
    try {
      setLoading(true);
      await api.post(`/itinerarios/${itinerario.id}/toggle-activo`);
      showSnackbar(
        `Itinerario ${itinerario.activo ? 'desactivado' : 'activado'} exitosamente`,
        'success'
      );
      fetchItinerarios();
    } catch (error) {
      showSnackbar('Error al cambiar estado', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon />
          Gestión de Itinerarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Nuevo Itinerario
        </Button>
      </Box>
      
      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estadoFilter}
                onChange={(e) => setEstadoFilter(e.target.value)}
                label="Estado"
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="activos">Activos</MenuItem>
                <MenuItem value="inactivos">Inactivos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              {filteredItinerarios.length} itinerario(s) encontrado(s)
            </Alert>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Paradas</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Distancia</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Duración Est.</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Cargando itinerarios...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredItinerarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography color="textSecondary">
                    No se encontraron itinerarios
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItinerarios
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((itinerario) => (
                  <TableRow
                    key={itinerario.id}
                    hover
                    sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {itinerario.nombre}
                      </Typography>
                      {itinerario.descripcion && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {itinerario.descripcion}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {itinerario.paradas?.slice(0, 3).map((parada, index) => (
                          <Chip
                            key={index}
                            label={parada}
                            size="small"
                            icon={<LocationOn />}
                            variant="outlined"
                          />
                        ))}
                        {itinerario.paradas?.length > 3 && (
                          <Chip
                            label={`+${itinerario.paradas.length - 3} más`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {itinerario.distancia_km ? (
                        <Chip
                          label={`${itinerario.distancia_km} km`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {itinerario.duracion_estimada ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2">
                            {itinerario.duracion_estimada} min
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={itinerario.activo ? 'Activo' : 'Inactivo'}
                        color={itinerario.activo ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleToggleActivo(itinerario)}
                        title={itinerario.activo ? 'Desactivar' : 'Activar'}
                      >
                        {itinerario.activo ? <ToggleOn /> : <ToggleOff />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(itinerario)}
                        title="Editar"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(itinerario.id)}
                        title="Eliminar"
                        disabled={loading}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredItinerarios.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>
      
      {/* Dialog Crear/Editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItinerario ? 'Editar Itinerario' : 'Nuevo Itinerario'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nombre del Itinerario"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Ej: Loma Grande - Emboscada"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="Descripción breve del recorrido..."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Distancia (km)"
                name="distancia_km"
                type="number"
                value={formData.distancia_km}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duración Estimada (minutos)"
                name="duracion_estimada"
                type="number"
                value={formData.duracion_estimada}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Paradas del Itinerario (mínimo 2)
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <List dense>
                  {formData.paradas.map((parada, index) => (
                    <ListItem key={index} disableGutters>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Parada ${index + 1}`}
                        value={parada}
                        onChange={(e) => handleParadaChange(index, e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <ListItemSecondaryAction>
                        {formData.paradas.length > 1 && (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveParada(index)}
                            color="error"
                          >
                            <RemoveCircle />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddParada}
                  sx={{ mt: 1 }}
                >
                  Agregar Parada
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={formData.activo}
                  onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.value }))}
                  label="Estado"
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {editingItinerario ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
